import { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import Modal from '../modal/modal';
import Card from '../card/Card';
import Input from '../input/Input';
import CurrencyInput from '../input/CurrencyInput';
import DropDown, { ItemProp } from '../dropdown/DropDown';
import DatePicker from '../datepicker/DatePicker';
import Toggle from '../toggle/Toggle';
import Span from '../texts/span/Span';
import { Share, ShareFormMetadata, ShareInput } from '../../types/interfaces';

interface ShareFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  shareToEdit?: Share | null;
  formMetadata: ShareFormMetadata | null;
  createShare: (
    input: ShareInput,
  ) => Promise<{ success: boolean; message?: string; status?: number }>;
  updateShare: (
    id: number,
    input: ShareInput,
  ) => Promise<{ success: boolean; message?: string; status?: number }>;
  isSaving: boolean;
}

type ShareFormErrors = {
  name?: string;
  ticker?: string;
  shareTypeId?: string;
  currencyId?: string;
  nominalValue?: string;
  issuedQuantity?: string;
  issueDate?: string;
};

const ShareFormModal = ({
  isOpen,
  onClose,
  onSuccess,
  shareToEdit = null,
  formMetadata,
  createShare,
  updateShare,
  isSaving,
}: ShareFormModalProps) => {
  const [name, setName] = useState('');
  const [ticker, setTicker] = useState('');
  const [shareTypeId, setShareTypeId] = useState<number | null>(null);
  const [currencyId, setCurrencyId] = useState<number | null>(null);
  const [nominalValue, setNominalValue] = useState<number | null>(null);
  const [issuedQuantity, setIssuedQuantity] = useState<number | null>(null);
  const [issueDate, setIssueDate] = useState('');
  const [enabled, setEnabled] = useState(true);
  const [errors, setErrors] = useState<ShareFormErrors>({});

  const isEditMode = Boolean(shareToEdit);

  const resetForm = () => {
    setName('');
    setTicker('');
    setShareTypeId(null);
    setCurrencyId(null);
    setNominalValue(null);
    setIssuedQuantity(null);
    setIssueDate('');
    setEnabled(true);
    setErrors({});
  };

  useEffect(() => {
    if (!isOpen) return;
    if (shareToEdit) {
      setName(shareToEdit.name ?? '');
      setTicker(shareToEdit.ticker ?? '');
      setShareTypeId(shareToEdit.shareTypeId ?? null);
      setCurrencyId(shareToEdit.currencyId ?? null);
      setNominalValue(shareToEdit.nominalValue ?? null);
      setIssuedQuantity(shareToEdit.issuedQuantity ?? null);
      setIssueDate(shareToEdit.issueDate ?? '');
      setEnabled(shareToEdit.enabled ?? true);
      setErrors({});
    } else {
      resetForm();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, shareToEdit]);

  const clearError = (field: keyof ShareFormErrors) => {
    setErrors((prev) => (prev[field] ? { ...prev, [field]: undefined } : prev));
  };

  const shareTypeItems: ItemProp[] = (formMetadata?.shareTypes ?? []).map((shareType) => ({
    id: String(shareType.id),
    label: shareType.name,
  }));

  const currencyItems: ItemProp[] = (formMetadata?.currencies ?? []).map((currency) => ({
    id: String(currency.id),
    label: currency.description,
  }));

  const selectedShareTypeTitle =
    shareTypeItems.find((item) => Number(item.id) === shareTypeId)?.label ?? 'Seleccione uno';
  const selectedCurrencyTitle =
    currencyItems.find((item) => Number(item.id) === currencyId)?.label ?? 'Seleccione uno';

  const handleShareTypeChange = (_title: string, item?: ItemProp) => {
    setShareTypeId(item ? Number(item.id) : null);
    clearError('shareTypeId');
  };

  const handleCurrencyChange = (_title: string, item?: ItemProp) => {
    setCurrencyId(item ? Number(item.id) : null);
    clearError('currencyId');
  };

  const handleTickerChange = (value: string) => {
    setTicker(value.trim().toUpperCase());
    clearError('ticker');
  };

  const validate = (): boolean => {
    const newErrors: ShareFormErrors = {};

    if (!name.trim()) {
      newErrors.name = 'El nombre es obligatorio';
    }
    if (!ticker.trim()) {
      newErrors.ticker = 'El ticker es obligatorio';
    }
    if (!shareTypeId) {
      newErrors.shareTypeId = 'Seleccioná el tipo de acción';
    }
    if (!currencyId) {
      newErrors.currencyId = 'Seleccioná la moneda';
    }
    if (nominalValue === null || nominalValue <= 0) {
      newErrors.nominalValue = 'El valor nominal debe ser mayor a 0';
    }
    if (issuedQuantity === null || issuedQuantity <= 0) {
      newErrors.issuedQuantity = 'La cantidad emitida debe ser mayor a 0';
    }
    if (!issueDate) {
      newErrors.issueDate = 'La fecha de emisión es obligatoria';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleSubmit = async () => {
    if (!validate()) return;

    const input: ShareInput = {
      name: name.trim(),
      ticker: ticker.trim().toUpperCase(),
      shareTypeId: shareTypeId as number,
      currencyId: currencyId as number,
      nominalValue: nominalValue as number,
      issuedQuantity: issuedQuantity as number,
      issueDate,
      enabled,
    };

    const result =
      isEditMode && shareToEdit
        ? await updateShare(shareToEdit.id, input)
        : await createShare(input);

    if (result.success) {
      toast.success(
        isEditMode ? 'Acción actualizada exitosamente' : 'Acción creada exitosamente',
      );
      resetForm();
      onClose();
      onSuccess?.();
      return;
    }

    if (result.status === 409) {
      setErrors((prev) => ({
        ...prev,
        ticker: result.message || 'Ya existe una acción con ese ticker en la empresa',
      }));
      return;
    }

    toast.error(
      result.message ||
        (isEditMode ? 'Error al actualizar la acción' : 'Error al crear la acción'),
    );
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={isEditMode ? 'Editar acción' : 'Nueva acción'}
      width='970px'
      primaryText={isEditMode ? 'Guardar' : 'Crear'}
      primaryVariant='primary'
      onPrimaryAction={handleSubmit}
      primaryActionDisabled={isSaving}
      secondaryText='Cancelar'
      secondaryVariant='contained'
      onSecondaryAction={handleClose}
      showOverlay={true}
    >
      <Card margin={{ top: '4' }}>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            columnGap: '24px',
            rowGap: '16px',
          }}
        >
          <div>
            <Input
              id='share-name'
              label='Nombre *'
              name='name'
              type='text'
              value={name}
              onChange={(e) => {
                setName(e.target.value);
                clearError('name');
              }}
              disabled={isSaving}
              size='md'
              inputStatus={errors.name ? 'error' : 'normal'}
              placeholder='Nombre'
              maxLength={120}
            />
            {errors.name && (
              <Span variant='danger' type='2'>
                {errors.name}
              </Span>
            )}
          </div>

          <div>
            <Input
              id='share-ticker'
              label='Ticker *'
              name='ticker'
              type='text'
              value={ticker}
              onChange={(e) => handleTickerChange(e.target.value)}
              disabled={isSaving}
              size='md'
              inputStatus={errors.ticker ? 'error' : 'normal'}
              placeholder='Ticker'
              maxLength={20}
            />
            {errors.ticker && (
              <Span variant='danger' type='2'>
                {errors.ticker}
              </Span>
            )}
          </div>

          <div>
            <DropDown
              id='share-type'
              label='Tipo de acción *'
              title={selectedShareTypeTitle}
              items={shareTypeItems}
              onChange={handleShareTypeChange}
              size='md'
              status={errors.shareTypeId ? 'error' : 'normal'}
              disabled={isSaving}
            />
            {errors.shareTypeId && (
              <Span variant='danger' type='2'>
                {errors.shareTypeId}
              </Span>
            )}
          </div>

          <div>
            <DropDown
              id='share-currency'
              label='Moneda *'
              title={selectedCurrencyTitle}
              items={currencyItems}
              onChange={handleCurrencyChange}
              size='md'
              status={errors.currencyId ? 'error' : 'normal'}
              disabled={isSaving}
            />
            {errors.currencyId && (
              <Span variant='danger' type='2'>
                {errors.currencyId}
              </Span>
            )}
          </div>

          <div>
            <CurrencyInput
              label='Valor nominal *'
              name='nominalValue'
              id='nominal-value'
              value={nominalValue}
              onValueChange={(value) => {
                setNominalValue(value);
                clearError('nominalValue');
              }}
              decimalScale={4}
              allowNegative={false}
              disabled={isSaving}
              size='md'
              inputStatus={errors.nominalValue ? 'error' : 'normal'}
              placeholder='0'
            />
            {errors.nominalValue && (
              <Span variant='danger' type='2'>
                {errors.nominalValue}
              </Span>
            )}
          </div>

          <div>
            <CurrencyInput
              label='Cantidad emitida *'
              name='issuedQuantity'
              id='issued-quantity'
              value={issuedQuantity}
              onValueChange={(value) => {
                setIssuedQuantity(value);
                clearError('issuedQuantity');
              }}
              decimalScale={0}
              allowNegative={false}
              disabled={isSaving}
              size='md'
              inputStatus={errors.issuedQuantity ? 'error' : 'normal'}
              placeholder='0'
            />
            {errors.issuedQuantity && (
              <Span variant='danger' type='2'>
                {errors.issuedQuantity}
              </Span>
            )}
          </div>

          <div>
            <DatePicker
              id='issue-date'
              label='Fecha de emisión *'
              placeholder='00/00/0000'
              defaultValue={issueDate}
              onChange={(date: string) => {
                setIssueDate(date);
                clearError('issueDate');
              }}
              disabled={isSaving}
              displayFormat='dd/MM/yyyy'
            />
            {errors.issueDate && (
              <Span variant='danger' type='2'>
                {errors.issueDate}
              </Span>
            )}
          </div>

          <div>
            <Span variant='neutral' type='4' modifier='strong'>
              Estado
            </Span>
            <Toggle
              id='share-enabled'
              checked={enabled}
              rightText='Activo'
              onChange={(checked: boolean) => setEnabled(checked)}
              disabled={isSaving}
            />
          </div>
        </div>
      </Card>
    </Modal>
  );
};

export default ShareFormModal;
